import * as React from 'react';
import { Check, ChevronsUpDown, Plus, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { dropdownNaturalScrollClassName, stopDropdownScrollPropagation } from '@/lib/dropdownNaturalScroll';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

interface SearchableSelectProps {
  value: string;
  onValueChange: (value: string) => void;
  options: { label: string; value: string }[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  disabled?: boolean;
  allowCustom?: boolean;
  createOptionLabel?: (value: string) => string;
}

export function SearchableSelect({
  value,
  onValueChange,
  options,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No results found.',
  className,
  disabled,
  allowCustom = false,
  createOptionLabel,
}: SearchableSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');

  const trimmedSearch = search.trim();

  const filtered = React.useMemo(() => {
    if (!trimmedSearch) return options;
    const q = trimmedSearch.toLowerCase();
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(q) || option.value.toLowerCase().includes(q),
    );
  }, [options, trimmedSearch]);

  const selectedLabel = options.find((option) => option.value === value)?.label ?? value;

  const hasExactMatch = React.useMemo(() => {
    if (!trimmedSearch) return false;
    const normalized = trimmedSearch.toLowerCase();
    return options.some(
      (option) =>
        option.label.toLowerCase() === normalized || option.value.toLowerCase() === normalized,
    );
  }, [options, trimmedSearch]);

  const showCreateOption = allowCustom && Boolean(trimmedSearch) && !hasExactMatch;

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setSearch('');
    }
  };

  const handleSelect = (nextValue: string) => {
    onValueChange(nextValue === value ? '' : nextValue);
    setOpen(false);
    setSearch('');
  };

  const handleCreate = () => {
    if (!trimmedSearch) return;
    onValueChange(trimmedSearch);
    setOpen(false);
    setSearch('');
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'w-full justify-between font-normal h-10',
            !value && 'text-muted-foreground',
            className,
          )}
        >
          <span className="truncate">{selectedLabel || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <div className="flex items-center border-b px-3 py-2">
          <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
          <input
            className="flex h-8 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            placeholder={searchPlaceholder}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <div
          className={cn(
            'max-h-[min(240px,calc(var(--radix-popover-content-available-height)-3rem))] p-1',
            dropdownNaturalScrollClassName,
          )}
          onWheel={stopDropdownScrollPropagation}
          onTouchMove={stopDropdownScrollPropagation}
        >
          {showCreateOption && (
            <button
              type="button"
              className="relative flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
              onClick={handleCreate}
            >
              <Plus className="mr-2 h-4 w-4 text-primary" />
              {createOptionLabel ? createOptionLabel(trimmedSearch) : `Add "${trimmedSearch}"`}
            </button>
          )}

          {filtered.length === 0 && !showCreateOption && (
            <p className="py-4 text-center text-sm text-muted-foreground">{emptyMessage}</p>
          )}

          {filtered.map((option) => (
            <button
              key={option.value}
              type="button"
              className={cn(
                'relative flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground',
                value === option.value && 'bg-accent text-accent-foreground',
              )}
              onClick={() => handleSelect(option.value)}
            >
              <Check
                className={cn(
                  'mr-2 h-4 w-4',
                  value === option.value ? 'opacity-100' : 'opacity-0',
                )}
              />
              {option.label}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
